(ns app.front.view.permit.form
  "The interactive UI for editing a verdict (permit, licence, registration) before issuing it."
  (:require [app.domain.application.model :as app-model]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.form.model :as form-model]
            [app.domain.form.schema :as form-schema]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.review.model :as review-model]
            [app.domain.util :as du]
            [app.domain.verdict.common-render :as common-render]
            [app.domain.verdict.common-util :as cutil]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.attachment.select-list :as attachment-select-list]
            [app.front.view.permit.conditions :as permit-conditions]
            [app.front.view.permit.draft-actions :as permit-draft-actions]
            [app.front.view.permit.inspections :as permit-inspections]
            [app.front.view.permit.permit-number-editor :as permit-number-editor]
            [app.front.view.permit.renewal :as renewal]
            [app.front.view.permit.sign-off :as permit-sign-off]
            [app.front.view.system-of-units.toggle :as sou-toggle]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [reagent.ratom]))


(rf/reg-sub ::permit-issuer-users :-> :app.front.view.permit.core/permit-issuer-users)

(rf/reg-sub ::fetching-issuer-users? :-> :app.front.view.permit.core/fetching-issuer-users?)


(rf/reg-sub ::form-data-schemas
  :<- [::ws-subs/application]
  (fn [app _]
    (->> app
         :entity/_parent
         (filter (eu/is-type-fn :entity.type/form))
         (keep :form/schema)
         (keep :form.schema/data-schema))))


(rf/reg-sub ::field-data
  :<- [::form-data-schemas]
  (fn [schemas [_ field]]
    (->> schemas
         (keep #(form-schema/find-field % field))
         first)))

(rf/reg-event-fx ::update-section
  (fn [{:keys [db]} [_ application-id section-id new-value]]
    {:db                (ee/set-value db section-id :verdict.section/content new-value)
     :dispatch-debounce [{:id      (str "section-changed-" section-id)
                          :timeout 500
                          :action  :dispatch
                          :event   [::process/signal
                                    application-id
                                    [:process.signal/update-verdict-draft {:verdict.section/content new-value
                                                                           :section-id              section-id}]
                                    {:on-done [::ee/save-done section-id :verdict.section/content new-value]}]}]}))

(rf/reg-event-fx ::set-issue-date
  (fn [{:keys [db]} [_ phase-id verdict-id new-date]]
    {:db                (ee/set-value db verdict-id :verdict/issue-date new-date)
     :dispatch-debounce [{:id      ::issue-date-changed
                          :timeout 500
                          :action  :dispatch
                          :event   [::process/signal
                                    phase-id
                                    [:process.signal/set-verdict-issue-date {:verdict-id         verdict-id
                                                                             :verdict/issue-date new-date}]
                                    {:on-done [::ee/save-done verdict-id :verdict/issue-date new-date]}]}]}))

(defn sort-users-by-full-name-and-id [persons user-id]
  (->> persons
       (map (fn [user]
              (let [full-name (user-model/full-name user)]
                (assoc user :full-name full-name))))
       (sort-by (juxt #(-> % :db/id (not= user-id))
                      :full-name))))

(rf/reg-sub
  ::sorted-users
  :<- [::permit-issuer-users]
  :<- [:session/user-id]
  (fn [[persons user-id] _]
    (sort-users-by-full-name-and-id persons user-id)))

(defmulti
  ^{:arglists '([ctx section])}
  render
  "Accepts a context and verdict section. Returns a DOM hierarchy in hiccup format."
  (fn [_ section] (:verdict.section/type section)))


(defmethod render :default [ctx section]
  (if (<sub [::ws-subs/loading-config?])
    (cc/skeleton-text)
    (common-render/render ctx section)))


(defn- render-children [ctx section]
  (for-frag [child (->> (:entity/_parent section)
                        (sort-by :ordered/order))]
    (render ctx child)))

(defn update-permitted? [section]
  (<sub [::process/app-signal-permitted?
         [:process.signal/update-verdict-draft {:section-id (:db/id section)}]]))

(defn- update-section [{:keys [application]} section new-value]
  (>evt [::update-section (:db/id application) (:db/id section) new-value]))


(defmethod render :verdict.section.type/template-page [ctx section]
  [:div.verdict-page.mt-6.mb-6.space-y-2
   (render-children ctx section)])


(defmethod render :verdict.section.type/cover-page [_ _]
  ; No front-end renderer for cover page
  [:div])


(defmethod render :verdict.section.type/cover-page-reviews [_ _]
  ; No front-end renderer for cover page reviews
  [:div])


(defmethod render :verdict.section.type/table-of-contents [_ _]
  ; No front-end renderer for table of contents
  [:div])


(defmethod render :verdict.section.type/forms [_ _]
  ; No front-end renderer for forms
  [:div])


(defmethod render :verdict.section.type/permit-category [_ _]
  ; No front-end renderer for permit-category
  [:div])


(defmethod render :verdict.section.type/permit-type [_ _]
  ; No front-end renderer for permit-type
  [:div])


(defmethod render :verdict.section.type/permit-set-label [_ _]
  ; No front-end renderer for permit-set-label
  [:div])

(defmethod render :verdict.section.type/bonds [_ _]
  ; No front-end renderer for bonds
  [:div])

(defmethod render :verdict.section.type/work-type [{:keys [tr workspace application lang]} _]
  (if (contains? ptc-model/permit-types-with-multiple-work-types (:workspace/permit-type workspace))
    [:div.verdict-page__section
     [:div.bx--label (tr [:verdict.section.type/work-type])]
     [:div.label-value (app-model/work-type-name application lang)]]
    [:div]))


(defmethod render :verdict.section.type/static-page [_ _]
  ; No front-end renderer for static page
  [:div])


(defmethod render :verdict.section.type/qr-code [_ _]
  ; No front-end renderer for QR code
  [:div])


(defmethod render :verdict.section.type/date-applied [_ _]
  ; No front-end renderer for date applied
  [:div])

(defmethod render :verdict.section.type/municipality [_ _]
  ; No front-end renderer for municipality
  [:div])

(defmethod render :verdict.section.type/subdivision [_ _]
  ; No front-end renderer for subdivision
  [:div])

(defmethod render :verdict.section.type/header [_ _]
  [:div])

(defmethod render :verdict.section.type/inspections [ctx section]
  [permit-inspections/view ctx section])

(defmethod render :verdict.section.type/general-conditions [ctx section]
  [permit-conditions/general-conditions-view ctx section])

(defmethod render :verdict.section.type/permit-specific-conditions [ctx section]
  [permit-conditions/permit-specific-conditions-view ctx section])

(defmethod render :verdict.section.type/attachments [{:keys [application tr verdict]} section]
  (r/with-let [*idx-vector (r/atom (or (:verdict.section/content section) []))]
    (let [{attachments     true
           bad-attachments false} (->> application
                                       :entity/_parent
                                       (filter (eu/is-type-fn :entity.type/attachment))
                                       (group-by attachment-model/is-valid?))]
      [:div.verdict-page.mt-6.mb-6 {:key (str (:db/id section))}
       [attachment-select-list/select-list
        {:attachments attachments
         :idx-vector  @*idx-vector
         :help-text   (when (du/in-state? :verdict.state/draft verdict)
                        (tr [:permit/attachment-missing-help]))
         :on-change   (fn [new-idx-vector]
                        (reset! *idx-vector new-idx-vector)
                        (>evt [::process/signal
                               application
                               [:process.signal/update-verdict-draft {:verdict.section/content new-idx-vector
                                                                      :section-id              (:db/id section)}]]))
         :editing?    (update-permitted? section)}]
       (when (seq bad-attachments)
         [ui/help
          (tr [:attachment/bad-files-permit]
              [(->> bad-attachments
                    (mapv (comp :attachment.version.file/filename
                                attachment-model/latest-file-version))
                    (str/join ", "))])])])))


(defmethod render :verdict.section.type/section [ctx section]
  [:div.verdict-page__section
   [:h4 (:verdict.section/content section)]
   (render-children (assoc ctx :section-title (:verdict.section/content section)) section)])


(defn- date-wrapper [{:keys [tr]} {:keys [value label-loc label-text disabled]
                                   :as   picker-opts}]
  (if (not disabled)
    [ui/date-picker picker-opts]
    (let [v (if (instance? reagent.ratom/RAtom value) @value value)]
      [:<>
       [:div.bx--label (or label-text (tr [label-loc]))]
       [:div.label-value (or (when (and v (> v 0))
                               ((<sub [::date-util/date-fn]) v))
                             "-")]])))


(defn- set-issue-date [{:keys [verdict application]} new-date]
  (>evt [::set-issue-date (:db/id application) (:db/id verdict) new-date]))


(defn issue-date-disabled? [verdict-id]
  (not (<sub [::process/app-signal-permitted?
              [:process.signal/set-verdict-issue-date
               {:verdict-id verdict-id}]])))


(defn issue-date
  "Issue date component used as a hardcoded subsection of a verdict section.
  See also `issue-date-section`."
  [{:keys [verdict tr] :as ctx}]
  (date-wrapper ctx {:id          "issue-date"
                     :label-loc   :verdict/issue-date
                     :tr          tr
                     :value       (:verdict/issue-date verdict)
                     :empty-value ""
                     :min-date    "2019-01-01"
                     :max-date    (date-util/now)
                     :class-name  "verdict-date-picker"
                     :disabled    (issue-date-disabled? (:db/id verdict))
                     :callback    #(set-issue-date ctx %)}))


(defmethod render :verdict.section.type/permit-base-data
  [{:keys [verdict application tr] :as ctx} _]
  (let [group-tz (<sub [::ws-subs/group-time-zone])]
    [:div
     [:div.bx--row
      [:div.verdict-static-label.responsive-column-half
       [:div.bx--label
        (tr [:verdict/municipal-permit-number])]
       [:div.label-value
        (or (:verdict/municipal-permit-number verdict)
            (app-model/workspace-id application))]]
      [:div.verdict-static-label.responsive-column-half
       [:div.bx--label
        (tr [:verdict.section.type/date-applied])]
       [:div.label-value
        (date-util/date-in-tz (:application/submitted application) group-tz)]]
      [:div.verdict-static-label.responsive-column-half
       (issue-date ctx)]
      [:div.verdict-static-label.responsive-column-half
       [:div.bx--label
        (tr [:verdict/status])]
       [:div.label-value
        (tr [(:domain/state verdict)])]]]
     (when (<sub [::process/app-signal-permitted? [:process.signal/set-verdict-issue-date
                                                   {:verdict-id (:db/id verdict)}]])
       [ui/help (tr [:permit/issue-date-help])])]))


(defn get-localized-section [ctx section]
  (or (->> (:loc-sects ctx)
           (filter #(= (:verdict.section/id %) (:verdict.section/id section)))
           first)
      section))


(defn money-input [{:keys [application] :as ctx} section]
  (let [section  (get-localized-section ctx section)
        value    (:verdict.section/content section)
        label    (:verdict.section/label section)
        currency (:verdict.section/currency section
                                            (-> application
                                                :entity/parent
                                                :domain/authority-group
                                                :group/currency))]
    (if (update-permitted? section)
      [ui/number-input {:id            (str (:db/id section))
                        :label-text    label
                        :step          10
                        :unit-label    currency
                        :default-value (/ (futil/parse-int value) 100)
                        :on-change     #(update-section ctx
                                                        section
                                                        ;; Added cents to the value by '* 100'
                                                        (or (when-let [parsed-num (-> % .-target .-value
                                                                                      (futil/parse-number 0.1))]
                                                              (js/Math.round (* 100 parsed-num)))
                                                            ""))}]
      [:div
       [:div.bx--label label]
       [:div.label-value (futil/money-string value currency)]])))


(defmethod render :verdict.section.type/currency-input [{:keys [rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    [:div.bx--row
     [:div.bx--col-xs-12.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
      (money-input ctx section)]]))


(defn authority-select [{:keys [tr application] :as ctx} section]
  (let [section           (get-localized-section ctx section)
        fetching?         (<sub [::fetching-issuer-users?])
        persons           (<sub [::sorted-users])
        user-id           (<sub [:session/user-id])
        {:keys [id name]} (:verdict.section/content section)
        label             (:verdict.section/label section)
        can-change?       (update-permitted? section)]
    (cond

      fetching?
      (cc/select-skeleton {})

      (and can-change? (seq persons))
      [:div
       [:div.bx--label
        (when (:verdict.section/required? section)
          {:class "required-field"})
        label]
       (cc/select {:id         (str (:db/id section))
                   :data-cy    "issuer"
                   :class-name "full-width-select"
                   :label-text ""
                   :value      (or id :none)
                   :on-change  #(>evt [::process/signal
                                       application
                                       [:process.signal/update-verdict-draft
                                        {:verdict.section/content (-> % futil/tval futil/parse-int)
                                         :section-id              (:db/id section)}]])}
                  (when-not id
                    [ui/placeholder-select-item tr :none])
                  (for-frag [usr persons]
                    (cc/select-item {:key   (str (:db/id usr))
                                     :text  (str (:full-name usr)
                                                 (when (= (:db/id usr) user-id)
                                                   (str " " (tr [:verdict/myself]))))
                                     :value (:db/id usr)})))]

      can-change?
      [ui/help (tr [:permit/no-issuer-users])]

      :else
      [:div
       [:div.bx--label label]
       [:div.label-value name]])))


(defmethod render :verdict.section.type/authority-user [{:keys [rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    [:div.bx--row
     [:div.bx--col-xs-12.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
      [authority-select ctx section]]]))


(defn- party-text [party lang]
  (str
    (party-model/simple-name party lang)
    " ("
    (->> (:party/party-roles party)
         (str/join ", "))
    ")"))


(defn party-select [{:keys [tr lang application] :as ctx} section]
  (let [section         (get-localized-section ctx section)
        localize-role   #(role-model/party-role-name % lang)
        parties         (->> (party-model/active-parties application)
                             (map (comp #(update % :party/party-roles (fn [roles] (sort (map localize-role roles))))
                                        eu/->map))
                             (sort-by #(-> % :party/party-roles first)))
        {:keys [id
                names]} (:verdict.section/content section)
        label           (:verdict.section/label section)
        can-change?     (update-permitted? section)]
    (cond
      (and can-change? (seq parties))
      [:div
       [:div.bx--label
        (when (:verdict.section/required? section)
          {:class "required-field"})
        label]
       (cc/select {:id         (str (:db/id section))
                   :data-cy    "issued-to"
                   :class-name "full-width-select"
                   :label-text ""
                   :value      (or id :none)
                   :on-change  #(>evt [::process/signal
                                       application
                                       [:process.signal/update-verdict-draft
                                        {:verdict.section/content (-> % futil/tval futil/parse-int)
                                         :section-id              (:db/id section)}]])}
                  (when-not id
                    [ui/placeholder-select-item tr :none])
                  (for-frag [party parties]
                    (cc/select-item {:key   (str (:db/id party))
                                     :text  (party-text party lang)
                                     :value (:db/id party)})))]

      can-change?
      [ui/help (tr [:permit/no-parties-users])]

      :else
      [:div
       [:div.bx--label label]
       [:div.label-value (i18n-model/get-text-from-entities-or-map names lang)]])))


(defmethod render :verdict.section.type/single-party [{:keys [rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    [:div.bx--row
     [:div.bx--col-xs-12.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
      (if (<sub [::ws-subs/loading-config?])
        (cc/select-skeleton)
        [party-select ctx section])]]))

(defn case-id-conflict [{tr :tr section-id :section-id :as ctx}]
  (let [year-only-renewal-numbering? (<sub [::renewal/year-only-renewal-numbering?])
        workspace-id                 (-> ctx :workspace :workspace/id)
        availability                 (<sub [::renewal/renewal-number-available workspace-id])
        unsaved?                     (and section-id (<sub [::ee/unsaved-for section-id]))]
    (when (and year-only-renewal-numbering?
               (not unsaved?)
               (:occupied? availability))
      [cc/inline-notification
       {:kind              :warning
        :hide-close-button true
        :title             (tr [:permit/potential-renewal-number-occupied-title])
        :subtitle          (tr [:permit/potential-renewal-number-occupied] [(:number availability)])}])))

(defn date-input [{:keys [tr rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    (let [section                    (get-localized-section ctx section)
          valid-until-date?          (= :valid-until-date (:verdict.section/id section))
          check-number-availability? (and (<sub [::renewal/year-only-renewal-numbering?])
                                          valid-until-date?
                                          (-> section :verdict.section/content str/blank? not))
          update-fn                  #(when (or (inst? %) (= % ::renewal/clear))
                                        (let [new-value (if (= % ::renewal/clear) "" %)]
                                          (if valid-until-date?
                                            (>evt [::renewal/update-renewal-valid-to
                                                   (-> ctx :application :db/id)
                                                   (-> ctx :workspace :workspace/id)
                                                   (:db/id section)
                                                   new-value])
                                            (update-section ctx section new-value))))
          can-change?                (update-permitted? section)]
      [:<>
       [:div.bx--row.items-end
        [:div.bx--col-xs-7.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
         ;; TODO WIN-2761 transform picked dates to group time zone!
         [date-wrapper ctx {:id          (str (:db/id section))
                            :required?   (boolean (:verdict.section/required? section))
                            :label-text  (:verdict.section/label section)
                            :value       (:verdict.section/content section)
                            :empty-value ""
                            :min-date    "2019-01-01"
                            :class-name  "verdict-date-picker"
                            :disabled    (not can-change?)
                            :callback    update-fn}]]
        (when can-change?
          [:div.bx--col-xs-5.bx--col-md-4.bx--col-lg-2.bx--col-xxl-1
           (cc/button {:kind     :tertiary
                       :size     :field
                       :disabled (-> section :verdict.section/content str/blank?)
                       :on-click #(update-fn ::renewal/clear)}
                      (tr [:clear]))])]
       (when (and can-change? check-number-availability?)
         [:div.bx--row
          [:div.bx--col-xs-7.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
           [case-id-conflict (assoc ctx :section-id (:db/id section))]]])])))


(defmethod render :verdict.section.type/date-input [ctx section]
  [date-input ctx section])


(defn issue-date-section
  "Issue date component as an independent verdict section. See also `issue-date`."
  [{:keys [tr rendered-ids verdict] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    (let [section     (get-localized-section ctx section)
          disabled    (issue-date-disabled? (:db/id verdict))
          is-required (boolean (:verdict.section/required? section))
          is-empty    (nil? (:verdict/issue-date verdict))
          format-date (<sub [::date-util/date-fn])]
      [:div.bx--row.items-end
       [:div.bx--col-xs-7.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
        ;; TODO WIN-2761 transform picked dates to group time zone!
        [date-wrapper ctx {:id          (str (:db/id section))
                           :required?   is-required
                           :label-text  (:verdict.section/label section)
                           :value       (:verdict/issue-date verdict)
                           :empty-value ""
                           :min-date    "2019-01-01"
                           :max-date    (date-util/now)
                           :class-name  "verdict-date-picker"
                           :placeholder (when (and is-empty (not is-required))
                                          (format-date (js/Date.)))
                           :disabled    disabled
                           :callback    #(set-issue-date ctx %)}]]
       (when-not disabled
         [:div.bx--col-xs-5.bx--col-md-4.bx--col-lg-2.bx--col-xxl-1
          (cc/button {:kind     :tertiary
                      :disabled is-empty
                      :on-click #(set-issue-date ctx "")}
                     (tr [:clear]))])])))


;; Issue date which can be used as an independent verdict section in a permit
;; template (see licensing). Building permits, for example, have section
;; :verdict.section.type/permit-base-data which contains issue-date
;; as a hardcoded component.
(defmethod render :verdict.section.type/issue-date
  [ctx section]
  [issue-date-section ctx section])


(defn- reviewers [{:keys [tr application]} _]
  [:div.bx--row
   [:div.verdict-static-label.bx--col-xs-12.bx--col-lg-9.bx--col-xxl-6
    [:div.bx--label
     (tr [:verdict/reviewers])]
    [:div.label-value
     (->> (review-model/phase-reviewer-names application)
          (str/join ", "))]]])


(defmethod render :verdict.section.type/reviewers
  [ctx section]
  (reviewers ctx section))


(defn- phrase-wrapper [{:keys [application workspace]} section]
  [ui-rt/editor-legacy {:id                      (:db/id section)
                        :label-text              (:verdict.section/label section)
                        :required?               (boolean (:verdict.section/required? section))
                        :initial-value           (<sub [::ee/attr-value (:db/id section) :verdict.section/content])
                        :on-change               (when (update-permitted? section) #(rf/dispatch-sync [::update-section (:db/id application) (:db/id section) %]))
                        :authority-group-id      (-> workspace :domain/authority-group :db/id)
                        :default-phrase-category (-> workspace :domain/authority-group :group/phrase-category-verdicts)}])


(defmethod render :verdict.section.type/text-area-with-phrases
  [{:keys [rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    (phrase-wrapper ctx (get-localized-section ctx section))))


(defmethod render :verdict.section.type/permit-sign-off
  [ctx _]
  [permit-sign-off/sign-off-section ctx])


(defn section->ids
  "Returns the verdict section ids of a section entity"
  [section]
  (->> section
       :entity/_parent
       (mapcat #(conj (eu/descendants :entity.type/verdict-section %) %))
       (keep :verdict.section/id)
       set))


(defn section->rendered-ids
  "Returns a map of 'section -> previously rendered ids'
  e.g. {1738945353234 #{}
        1738945353235 #{:cover-page-permit-data :permit-set-label}}"
  [sections]
  (->> sections
       (reductions
         (fn [acc section]
           (set/union acc (section->ids section)))
         #{})
       (zipmap (map :db/id sections))))


(defn form [workspace verdict]
  (let [lang         (<sub [:language])
        tr           (<sub [::ws-subs/tr])
        format-date  (<sub [::date-util/date-fn])
        application  (workspace-model/workspace-application workspace)
        execution    (workspace-model/workspace-execution workspace)
        sections     (->> verdict
                          :entity/_parent
                          (filter #(-> % :entity/type (= :entity.type/verdict-section)))
                          (sort-by :ordered/order))
        ;; map of :verdict.section/ids rendered before a specific section
        rendered-ids (section->rendered-ids sections)
        loc-sects    (->> sections
                          (filter #(= (:entity/language %) lang))
                          (mapcat #(eu/descendants :entity.type/verdict-section %))
                          (filter :verdict.section/id))
        phrases      (<sub [::entities/entities {:entity/type            :entity.type/phrase
                                                 :domain/authority-group (-> workspace
                                                                             :domain/authority-group
                                                                             :db/id)}])
        user         (<sub [:session/user])
        ctx          (util/mmap application
                                execution
                                workspace
                                verdict
                                user
                                phrases
                                tr
                                loc-sects
                                lang
                                format-date)]
    [:section
     {:key (str (:db/id verdict))}
     [:h2 (tr [(:workspace/permit-type workspace)])]
     [:h3 (tr [(:domain/state verdict)])]
     [permit-number-editor/editor ctx]
     [:div
      (for-frag [section sections
                 :when (not (:verdict.section/copy-of section))]
        (render (assoc ctx :rendered-ids (get rendered-ids (:db/id section))) section))]
     [permit-draft-actions/draft-actions ctx sections]]))


;; ---------------------------
;; Public Works renderers
;; ---------------------------

(defmethod render :verdict.section.type/permit-information
  [{:keys [tr workspace verdict] :as ctx} section]
  (let [sections         (:entity/_parent section)
        expiry-section   (p/find-first sections {:verdict.section/id :expiry-date})
        issuer-section   (p/find-first sections {:verdict.section/id :issuer})
        reviewer-section (p/find-first sections {:verdict.section/id :reviewer})
        prim-prop        (-> workspace :workspace/primary-property)
        property-type    (:property/type prim-prop)]
    [:div.verdict-page__section
     [:h4 (tr [:verdict.section.type/permit-information])]
     ;; Permit number - Location of work - Legal
     [:div.bx--row
      [:div.verdict-static-label.responsive-column-full
       [:div.bx--label
        (tr [:verdict/municipal-permit-number])]
       [:div.label-value
        (:verdict/municipal-permit-number verdict)]]
      [:div.verdict-static-label.responsive-column-full
       [:div.bx--label
        (tr [:verdict/location-of-work])]
       [:div.label-value
        (:workspace/address workspace)]]
      [:div.verdict-static-label.bx--col-xs-12.bx--col-md-4.bx--col-lg-6
       [:div.bx--label
        (if (= property-type :property.type/road)
          (tr [:property/description])
          (tr [:legal]))]
       [:div.label-value
        (if (= property-type :property.type/road)
          (tr [:property/from-to]
              [(:property/from-st prim-prop)
               (:property/to-st prim-prop)])
          (workspace-model/property-data workspace :property/legal))]]]
     ;; Date issued - Expiry date
     [:div.bx--row
      [:div.verdict-static-label.responsive-column-full
       [issue-date ctx]]
      (when expiry-section
        [:div.verdict-static-label.responsive-column-full
         (date-wrapper ctx {:tr          tr
                            :value       (:verdict.section/content expiry-section)
                            :empty-value ""
                            :label-loc   :verdict/expiry-date
                            :min-date    "2019-01-01"
                            :disabled?   (update-permitted? expiry-section)
                            :class-name  "verdict-date-picker"
                            :callback    (partial update-section ctx expiry-section)})])]
     ;; Issued by - Reviewed by
     [:div.bx--row
      (when issuer-section
        [:div.verdict-static-label.responsive-column-full
         [authority-select ctx issuer-section]])
      (when reviewer-section
        [:div.verdict-static-label.responsive-column-full
         [authority-select ctx reviewer-section]])]]))


(defmethod render :verdict.section.type/fees-and-payments
  [{:keys [tr application] :as ctx} section]
  (let [indemnity-section (some-> section
                                  :entity/_parent
                                  (p/find-first {:verdict.section/id :indemnity-amount}))
        indemnity-label   (tr [:verdict/indemnity-amount])
        fees              (some-> (:entity/_parent application)
                                  ;; TODO does not work with new invoices!
                                  (p/find-first {:entity/type :entity.type/legacy-invoice})
                                  :entity/_parent)
        fees              (filter #(= (:entity/type %) :entity.type/legacy-fee) fees)]
    [:div.verdict-page__section
     [:h4 (tr [:verdict.section.type/fees-and-payments])]
     ;; Fees
     (for-frag [{desc     :fee/description
                 amount   :fee/amount
                 currency :fee/currency} fees]
       [:div.bx--row
        [:div.responsive-column-full desc]
        [:div.responsive-column-full
         (futil/money-string amount currency)]])
     (when indemnity-section
       (if (update-permitted? indemnity-section)
         [:div.bx--row
          [:div.fee-align.responsive-column-full
           indemnity-label]
          [:div.responsive-column-full
           (money-input ctx indemnity-section)]]
         (when-let [money (futil/money-string (:verdict.section/content indemnity-section)
                                              (:verdict.section/currency indemnity-section))]
           [:div.bx--row
            [:div.responsive-column-full indemnity-label]
            [:div.responsive-column-full money]])))]))


(defmethod render :verdict.section.type/permit-number
  [_ _]
  ;; No front-end renderer needed
  [:div])


(defmethod render :verdict.section.type/permission-granted
  [{:keys [lang tr application] :as ctx} _]
  (let [applicants (->> application
                        :entity/_parent
                        (filter #(party-model/has-role? % :role/applicant)))]

    [:div.verdict-page__section
     [:h4 (tr [:verdict.section.type/permission-granted])]
     [:div.bx--row
      (map-indexed (fn [i party]
                     [:div.grantee.bx--col-xs-12.bx--col-sm-6.bx--col-md-4.bx--col-xxl-3
                      {:key (str "grantee-" i)}
                      [:div (cutil/affiliation party lang)]
                      [:div (party-model/get-user-key ctx party :user/street-address)]
                      [:div (cutil/munizip ctx party)]])
                   applicants)]]))


(defmethod render :verdict.section.type/work-description
  [{tr :tr :as ctx} section]
  [:div.verdict-page__section
   [:h4 (tr [:verdict.section.type/work-description])]
   [phrase-wrapper ctx section]])


(defmethod render :verdict.section.type/permit-description
  [{:keys [tr verdict] :as ctx} section]
  (let [permit-description (cutil/get-permit-description-form-field-value verdict)]
    [:div.verdict-page__section
     [:div.bx--label (tr [:verdict.section.type/permit-description])]
     (if (str/blank? permit-description)
       [phrase-wrapper ctx section]
       [ui-rt/editor-legacy {:id            "permit-description"
                             :initial-value permit-description}])]))


(defn number-input [ctx section]
  (let [section (get-localized-section ctx section)    value (:verdict.section/content section)
        label   (:verdict.section/label section)
        unit    (:verdict.section/number-unit section)]
    (if (update-permitted? section)
      [ui/number-input {:id            (str (:db/id section))
                        :required      (boolean (:verdict.section/required? section))
                        :label-text    label
                        :step          0.1
                        :unit-label    unit
                        :default-value (futil/parse-number value 0.1)
                        :on-change     #(update-section ctx section (-> % .-target .-value (futil/parse-number 0.1)))}]
      [:<>
       [:div.bx--label label]
       [:div.label-value (str value " " unit)]])))


(defmethod render :verdict.section.type/number-input [{:keys [rendered-ids] :as ctx} section]
  (when (not (contains? rendered-ids (:verdict.section/id section)))
    [:div.bx--row
     [:div.bx--col-xs-12.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
      [number-input ctx section]]]))


(defmethod render :verdict.section.type/cover-page-permit-data
  [_ _]
  ;; No front-end renderer needed
  [:div])


(defn- render-table-cell-value [{:keys [tr group-tz]} {:keys [tag value rich-text? unit unit-label form-field] :as cell}]
  (let [rendered-value (if rich-text?
                         [ui-rt/editor-legacy {:id            (str "table-cell-value-" (:db/id form-field))
                                               :initial-value value}]
                         (common-render/render-form-field-value (util/mmap tag value tr group-tz unit)))
        render-unit    (or unit-label
                           (<sub [::sou-toggle/label-in-default-system unit]))]
    (if (some? render-unit)
      (when (common-render/cell-value-entered? cell)
        [:span rendered-value " " render-unit])
      rendered-value)))


(defn- form-field-table [{:keys [verdict] :as ctx} field]
  (let [{:keys [headers rows]} (form-model/table-field-data-for-ws (verdict-model/get-workspace verdict) field)
        group-tz               (-> verdict :entity/parent :domain/authority-group :group/tz)
        cell-ctx               (assoc ctx :group-tz group-tz)
        ;; Drop wholly-empty rows (judged from raw values); every cell of a kept row is
        ;; still rendered so the columns stay aligned.
        rows-with-values       (filter #(some common-render/cell-value-entered? %) rows)]
    (when (seq rows-with-values)
      [:table.verdict-form-field-table.w-full.table-fixed.border-collapse
       [:thead
        (into [:tr]
              (for [header headers]
                [:th.bg-gray-200.border-b.border-gray-400.p-2.text-left.align-top.break-words.text-xs header]))]
       (into [:tbody]
             (for [row rows-with-values]
               (into [:tr]
                     (for [cell row]
                       [:td.border-b.border-gray-300.p-2.align-top.break-words
                        (render-table-cell-value cell-ctx cell)]))))])))


(defmethod render :verdict.section.type/form-field [{:keys [tr verdict lang]} section]
  (let [field                                          (:verdict.section/content section)
        {:keys [label unit unit-label tag rich-text?]} (<sub [::field-data field])
        value                                          (form-model/form-field-value-for-ws (verdict-model/get-workspace verdict) field)
        group-tz                                       (-> verdict :entity/parent :domain/authority-group :group/tz)
        render-unit                                    (if (some? unit-label)
                                                         unit-label
                                                         (<sub [::sou-toggle/label-in-default-system unit]))
        render-value                                   (common-render/render-form-field-value (util/mmap tag value tr group-tz unit))]
    (cond
      (and (= tag :table)
           (ff/enabled? :feature-flag.id/permit-pdf-table-form-fields))
      [form-field-table (util/mmap tr verdict) field]

      (= tag :table)
      nil

      :else
      [:div.verdict-static-label
       [:div.bx--label (form-model/label-text label lang)]
       (when (not (str/blank? render-value))
         (if (some? render-unit)
           [:div.label-value render-value " " render-unit]
           [:div.label-value (if rich-text? [ui-rt/editor-legacy {:id            (str "value-" field)
                                                                  :initial-value render-value}]
                                 render-value)]))])))
